import { RocketRequestTimeoutError } from '@/errors/RocketRequestTimeoutError';
import { DEFAULT_REQUEST_RESPONSE_TIMEOUT_MS } from './constants';
import { rocketIntegrationLogger } from './logger';
import { processMessageEvent } from './messageEventProcessing';
import { ExpectedRequestFieldValues, RiRequest, RiResponse, WindowEventListener } from './types';

const logger = rocketIntegrationLogger;

/**
 * Basic send request, no waiting for response. Generates a request ID and
 * sends it
 *
 * @returns  The request ID that was generated
 */
export function sendRequestToRocket(
  iFrame: HTMLIFrameElement,
  requestWithoutId: Omit<RiRequest, 'requestId'>
) {
  const contentWindow = iFrame?.contentWindow;
  if (!contentWindow) throw new Error('No contentWindow for rocket iFrame found');

  const requestId = window.crypto.randomUUID() as string;

  const request = { requestId, ...requestWithoutId } as RiRequest;
  logger.debug(`Sending '${request.name}' request to rocket (requestId=${requestId})`, request);
  contentWindow.postMessage(
    JSON.stringify({
      from: 'listmanager',
      to: 'rocket',
      request,
    }),
    '*'
  );

  return requestId;
}

/** Basic send response*/
export function sendResponseToRocket(
  iFrame: HTMLIFrameElement,
  requestId: string,
  responseWithoutId: Omit<RiResponse, 'requestId'>
) {
  const contentWindow = iFrame?.contentWindow;
  if (!contentWindow) throw new Error('No contentWindow for rocket iFrame found');

  const response = { requestId, ...responseWithoutId };
  // LIST-905: console.log('sendResponseToRocket');
  // LIST-905: console.log(response);
  logger.debug(`Sending response to rocket (requestId=${requestId})`, response);
  contentWindow.postMessage(
    JSON.stringify({
      from: 'listmanager',
      to: 'rocket',
      response,
    }),
    '*'
  );
}

/**
 * Send request and wait for response from rocket. If timeOut milliseconds pass
 * before receiving message, the Promise will reject.
 */
export function sendRequestToRocketAndWaitForResponse(
  iFrame: HTMLIFrameElement,
  requestWithoutId: Omit<RiRequest, 'requestId'>,
  timeoutMs = DEFAULT_REQUEST_RESPONSE_TIMEOUT_MS
): Promise<RiResponse> {
  return new Promise((resolve, reject) => {
    const contentWindow = iFrame?.contentWindow;
    if (!contentWindow) throw new Error('No contentWindow for rocket iFrame found');

    let timeout = null as unknown as number;

    const requestId = sendRequestToRocket(iFrame, requestWithoutId);

    const listener = (event: MessageEvent) => {
      const message = processMessageEvent(event);
      if (message?.from === 'rocket' && message?.response?.requestId === requestId) {
        if (timeout !== null) window.clearTimeout(timeout);
        window.removeEventListener('message', listener);
        resolve(message.response);
      }
    };

    timeout = window.setTimeout(() => {
      window.removeEventListener('message', listener);
      reject(
        new RocketRequestTimeoutError(`Request ${requestId} timed out waiting a response`, {
          requestId,
          ...requestWithoutId,
        } as RiRequest)
      );
    }, timeoutMs);

    window.addEventListener('message', listener);
  });
}

const expectedRequestListeners = new Map<string, WindowEventListener>();

const requestMatchesExpected = (
  expectedRequestFieldValues: ExpectedRequestFieldValues,
  request: RiRequest
) => Object.entries(expectedRequestFieldValues).every(([key, value]) => request[key] === value);

/**
 * Wait for a request to come from rocket that has the same values for each specified field ()
 */
export function waitForExpectedRequestFromRocket(
  iFrame: HTMLIFrameElement,
  expectedFieldValues: ExpectedRequestFieldValues
) {
  return new Promise<void>((resolve) => {
    const contentWindow = iFrame?.contentWindow;
    if (!contentWindow) throw new Error('No contentWindow for rocket iFrame found');

    const requestListener = (event: MessageEvent) => {
      const message = processMessageEvent(event);
      console.log('waitForExpectedRequestFromRocket - processMessageEvent');
      console.log(message);
      const request = message?.request;
      if (
        message?.from === 'rocket' &&
        request &&
        requestMatchesExpected(expectedFieldValues, request)
      ) {
        stopWaitingForExpectedRequestFromRocket(expectedFieldValues);
        resolve();
      }
    };

    const key = JSON.stringify(expectedFieldValues);
    expectedRequestListeners.set(key, requestListener);

    logger.debug(`Waiting for expected '${expectedFieldValues.name}' request from rocket`, {
      expectedFieldValues,
    });

    window.addEventListener('message', requestListener);
  });
}

export function stopWaitingForExpectedRequestFromRocket(
  expectedFieldValues: ExpectedRequestFieldValues
) {
  const key = JSON.stringify(expectedFieldValues);
  const requestListener = expectedRequestListeners.get(key);
  if (requestListener) {
    logger.debug(
      `Stopping waiting for expected '${expectedFieldValues.name}' request from rocket`,
      {
        expectedFieldValues,
      }
    );
    window.removeEventListener('message', requestListener);
    expectedRequestListeners.delete(key);
  }
}
